import React, { MutableRefObject, useContext, useEffect } from 'react';
import { BaseEvent, BrowserOptions, EventOptions } from '@amplitude/analytics-types';
import { AmplitudeProvider as SparkAmplitudeProvider, useAmplitudeContext } from '@athena/spark-amplitude';

type EventObserver = (event: string, data: { [key: string]: string }) => void;

class EventObservers {
  private observers = [] as EventObserver[];

  addObserver = (observer: EventObserver): void => {
    if (!this.observers.includes(observer)) {
      this.observers.push(observer);
    }
  };
  removeObserver = (observer: EventObserver): void => {
    this.observers.splice(this.observers.indexOf(observer), 1);
  };
  dispatch = (event: string, data: { [key: string]: string }): void => {
    this.observers.forEach((observer: EventObserver) => observer(event, data));
  };
}

export const eventObservers = new EventObservers();

export const getEventName = ({
  productName,
  pageName,
  object,
  action,
  pageNameInProps,
}: {
  productName: string;
  pageName: string;
  action: string;
  object?: string;
  pageNameInProps?: boolean;
}) => {
  // prefix event name with ProductName
  let objectPrefix = productName;
  // by default, append pageName to the event name.  if pageNameInProps=true,
  // include in the event properties instead
  !pageNameInProps && (objectPrefix += `_${pageName}`);
  // if object is provided, append it to the event name, and finally append the action
  const eventName = `${objectPrefix}${object ? '_' + object : ''}::${action}`;
  return eventName;
};

interface AmplitudeEvent {
  eventInput: string | BaseEvent;
  eventProperties?: Record<string, any>;
  eventOptions?: EventOptions;
}

export interface SparkAmplitudeConfig extends BrowserOptions {
  productName: string;
  apiKey: string;
  contextId?: string;
}

type AmplitudeProviderState = {
  productNameRef: MutableRefObject<string>;
  eventQueueRef: MutableRefObject<AmplitudeEvent[]>;
  pageNameResolver?: () => string;
};

type AmplitudeProviderProps = SparkAmplitudeConfig & {
  children?: React.ReactNode;
  productName?: string;
  pageNameResolver?: () => string;
};

const AmplitudeContext = React.createContext<AmplitudeProviderState | undefined>(undefined);

type AmpTrack = (
  ...args: Parameters<ReturnType<typeof useAmplitudeContext>['track']>
) => ReturnType<ReturnType<typeof useAmplitudeContext>['track']> | undefined;

type AmpTrackEvent = ({
  action,
  object,
  pageName,
  data,
}: {
  action: string;
  object?: string;
  pageName?: string;
  pageNameInProps?: boolean;
  data?: Record<string, any>;
}) => void;

type AmpTrackPageViewParams = { pageName?: string; data?: Record<string, any> };
type AmpTrackPageView = (params?: AmpTrackPageViewParams) => void;

export const AmplitudeProvider = ({ children, ...config }: AmplitudeProviderProps) => {
  const { productName, apiKey, contextId, pageNameResolver } = config;
  const productNameRef: MutableRefObject<string> = React.useRef(productName);
  const eventQueueRef: MutableRefObject<AmplitudeEvent[]> = React.useRef([]);

  const contextValue = {
    pageNameResolver,
    productNameRef,
    eventQueueRef,
  };

  return (
    <AmplitudeContext.Provider value={contextValue}>
      {/* tried to spread {...config} here but it caused TS annotation errors that couldn't be resolved easily */}
      <SparkAmplitudeProvider productName={productName} apiKey={apiKey} contextId={contextId}>
        {children}
      </SparkAmplitudeProvider>
    </AmplitudeContext.Provider>
  );
};

// properties to send with each event that are determined
// during or after app initialization
export const ampGlobalProps: { [key: string]: string } = {};

type UseAmplitude = (pageName?: string) => ReturnType<typeof useAmplitudeContext> & {
  getProductName: () => string;
  setProductName: (productName: string) => void;
  pageName: string;
  ampTrack: AmpTrack;
  ampTrackEvent: AmpTrackEvent;
  ampTrackPageView: AmpTrackPageView;
};

export const useAmplitude: UseAmplitude = (pageName?: string) => {
  const { productNameRef, eventQueueRef, pageNameResolver } = useContext(AmplitudeContext) || {
    productNameRef: { current: '' },
    eventQueueRef: { current: [] },
    pageNameResolver: undefined,
  };

  const amplitudeContext = useAmplitudeContext();
  const { getSessionId, track, setUserProperties } = amplitudeContext;
  const ampSessionId = getSessionId();
  pageName = pageName || (pageNameResolver && pageNameResolver()) || 'Unknown';

  const ampTrack: AmpTrack = (eventInput, eventProperties?, eventOptions?: EventOptions) => {
    if (!getSessionId()) {
      // add event to queue until amplitude session id is known
      eventQueueRef.current.push({ eventInput, eventProperties, eventOptions });
    } else if (!productNameRef.current) {
      console.error(`Error: Attempting to track amplitude event before product name is set - ${eventInput}`, {
        ...ampGlobalProps,
        ...eventProperties,
      });
    } else {
      eventObservers.dispatch(eventInput as string, { ...ampGlobalProps, ...eventProperties });
      return track(eventInput, { ...ampGlobalProps, ...eventProperties }, eventOptions);
    }
  };

  const ampTrackEvent: AmpTrackEvent = ({ action, object, data, pageName: pageNameOverride, pageNameInProps }) => {
    const eventInput = getEventName({
      productName: productNameRef.current,
      pageName: (pageNameOverride || pageName) as string,
      action,
      object,
      pageNameInProps,
    });

    ampTrack(eventInput, {
      ...data,
      ...(pageNameInProps && { 'Common::PageName': pageNameOverride || pageName }),
    });
  };

  const ampTrackPageView: AmpTrackPageView = ({ pageName: pageNameOverride, data } = {}) => {
    ampTrack(`${productNameRef.current}::PageView`, {
      'Common::PageName': (pageNameOverride || pageName) as string,
      ...data,
    });
  };

  const getProductName = () => productNameRef.current;
  const setProductName = (productName: string) => {
    productNameRef.current = productName;
    setUserProperties({ productName });
  };

  useEffect(() => {
    // flush any events that were tracked before the session id was set
    if (ampSessionId && eventQueueRef.current.length > 0) {
      eventQueueRef.current.forEach(({ eventInput, eventProperties, eventOptions }) => {
        track(eventInput, { ...ampGlobalProps, ...eventProperties }, eventOptions);
      });
      eventQueueRef.current = [];
    }
  }, [eventQueueRef, ampSessionId, track]);

  return {
    ...amplitudeContext,
    ampTrack,
    ampTrackEvent,
    ampTrackPageView,
    getProductName,
    setProductName,
    pageName,
  };
};
